import {useContext, useState} from 'react';
import CommentDivider from './CommentDivider';
import CommentSection from './CommentSection';
import ReportModal from './ReportModal';
import FetchList from '../../common/container/FetchList';
import {showAuthModal} from '../../common/function';
import useLogin from '../../common/hook/useLogin';
import {HEADER_HEIGHT} from '../../common/scss/util/variable';
import ListEmptyContent from '../../commonSub/component/ListEmptyContent';
import {CommentThemeContext} from '../common';
import {IComment, ICommentLikeStatus, ICommentSection} from '../type';

interface IProps {
  ids: number[];
  sectionMap: {[id: number]: ICommentSection};
  commentMap: {[id: number]: IComment};
  fetchCommentsActionType: string;
  fetchCommentsFetchKey: number;
  emptyTextKey?: string;
  requestGetNextList: () => void;
  fetchReplyComments: (parentId: number, isFirst?: boolean) => void;
  postReplyComment?: (parentId: number, content: string) => void;
  deleteComment: (id: number) => void;
  changeLikeStatus: (id: number, likeStatus: ICommentLikeStatus) => void;
  toggleReplyComments: (id: number, isOpen: boolean) => void;
  contentId?: number;
}

export default function CommentList({
  ids,
  sectionMap,
  commentMap,
  fetchCommentsActionType,
  fetchCommentsFetchKey,
  emptyTextKey,
  requestGetNextList,
  fetchReplyComments,
  postReplyComment,
  deleteComment,
  changeLikeStatus,
  toggleReplyComments,
  contentId,
}: IProps) {
  const [reportingId, setReportingId] = useState<number>();
  const {isLogin} = useLogin();
  const {emptyTextStyle, commentDividerColor} = useContext(CommentThemeContext);

  const openReportModal = (id: number) => {
    if (isLogin) setReportingId(id);
    else showAuthModal();
  };

  return (
    <>
      <FetchList
        items={ids}
        actionType={fetchCommentsActionType}
        fetchKey={fetchCommentsFetchKey}
        noRowsRenderer={() => (
          <ListEmptyContent
            excludingSize={HEADER_HEIGHT + INPUT_HEIGHT}
            textKey={emptyTextKey ?? 'comment_empty_first'}
            textClassName={emptyTextStyle ?? 's16-regular-grey08'}
          />
        )}
        requestGetNextList={requestGetNextList}
      >
        {index => {
          const id = ids[index];
          const comment = commentMap[id];
          const {isOpen, replyIds, isEnd} = sectionMap[id];
          const replyComments = replyIds.map(id => commentMap[id]);
          const isPrevOpen = index > 0 && sectionMap[ids[index - 1]].isOpen;
          return (
            <>
              {index > 0 && !isPrevOpen && (
                <CommentDivider lineColor={commentDividerColor} />
              )}
              <CommentSection
                comment={comment}
                contentId={contentId}
                isOpen={isOpen}
                replyComments={replyComments}
                isEnd={isEnd}
                openReportModal={openReportModal}
                fetchReplyComments={fetchReplyComments}
                postReplyComment={postReplyComment}
                deleteComment={deleteComment}
                changeLikeStatus={changeLikeStatus}
                toggleReplyComments={toggleReplyComments}
              />
            </>
          );
        }}
      </FetchList>
      {reportingId !== undefined && (
        <ReportModal
          commentId={reportingId}
          onRequestClose={() => setReportingId(undefined)}
        />
      )}
    </>
  );
}

const INPUT_HEIGHT = 92;
